/**
 * Todo Assignment Service
 * Handles task assignment and ownership operations
 * Part of the modularized todo service architecture
 */

import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from '../service-registry';
import { typedSupabase } from '@/lib/typed-supabase';
import { Todo } from '@/types/todo';
import { taskActivityService } from '@flowstate/core';
import { notificationManager } from '@flowstate/core';
import { NOTIFICATION_TYPES } from '@/types/notification';
import { logger } from '@/lib/logger';

export interface ITodoAssignmentService extends IService {
  assignTodo(id: string, assignedTo: string, assignerId: string): Promise<ServiceResponse<Todo>>;
  unassignTodo(id: string): Promise<ServiceResponse<Todo>>;
  getAssignedTodos(userId: string): Promise<ServiceResponse<Todo[]>>;
  getProfileTasks(userId: string): Promise<ServiceResponse<Todo[]>>;
  getProfileSubtasks(parentId: string): Promise<ServiceResponse<Todo[]>>;
}

export class TodoAssignmentService implements ITodoAssignmentService {
  readonly name = 'TodoAssignmentService';
  private initialized = false;

  async initialize(): Promise<void> {
    this.initialized = true;
    logger.info('TodoAssignmentService initialized');
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info('TodoAssignmentService disposed');
  }

  async assignTodo(id: string, assignedTo: string, assignerId: string): Promise<ServiceResponse<Todo>> {
    try {
      logger.debug('Assigning todo', { id, assignedTo, assignerId });

      // Get current todo for context
      const { data: currentTodo, error: fetchError } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        logger.error('Failed to fetch todo for assignment', { error: fetchError.message, id });
        return createErrorResponse('Todo not found');
      }

      // Update assignment
      const { data, error } = await typedSupabase
        .from('todos')
        .update({
          assigned_to: assignedTo,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Failed to assign todo', { error: error.message, id, assignedTo });
        throw error;
      }

      const updatedTodo = data as Todo;

      // Log activity
      try {
        await taskActivityService.logTaskActivity({
          taskId: id,
          actionType: 'updated',
          fieldName: 'assigned_to',
          targetUserId: assignedTo,
          details: {
            assigned_to: assignedTo,
            previous_assigned_to: currentTodo.assigned_to,
          },
        });
      } catch (activityError) {
        logger.warn('Failed to log assignment activity', { error: activityError, id });
        // Don't fail the assignment for activity logging issues
      }

      // Send notification if assigned to someone else
      if (assignedTo && assignedTo !== assignerId) {
        try {
          await notificationManager.createNotification({
            type: NOTIFICATION_TYPES.TASK_ASSIGNED,
            user_id: assignedTo,
            title: 'Task Assigned',
            actor_id: assignerId,
            entity_type: 'todo',
            entity_id: id,
            message_preview: `You have been assigned: ${updatedTodo.title}`,
            data: {
              todo_id: id,
              assigned_by: assignerId,
            },
          });
        } catch (notificationError) {
          logger.warn('Failed to send assignment notification', { error: notificationError, id });
          // Don't fail assignment for notification issues
        }
      }

      logger.info('Todo assigned successfully', { id, assignedTo, assignerId });
      return createSuccessResponse(updatedTodo);

    } catch (error) {
      logger.error('Error in assignTodo', {
        error: error instanceof Error ? error.message : error,
        id,
        assignedTo,
        assignerId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Assign todo failed');
    }
  }

  async unassignTodo(id: string): Promise<ServiceResponse<Todo>> {
    try {
      logger.debug('Unassigning todo', { id });

      // Get current todo for context
      const { data: currentTodo, error: fetchError } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        logger.error('Failed to fetch todo for unassignment', { error: fetchError.message, id });
        return createErrorResponse('Todo not found');
      }

      // Update assignment to null
      const { data, error } = await typedSupabase
        .from('todos')
        .update({
          assigned_to: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Failed to unassign todo', { error: error.message, id });
        throw error;
      }

      const updatedTodo = data as Todo;

      // Log activity
      try {
        await taskActivityService.logTaskActivity({
          taskId: id,
          actionType: 'updated',
          fieldName: 'assigned_to',
          targetUserId: currentTodo.assigned_to ?? undefined,
          details: {
            previous_assigned_to: currentTodo.assigned_to,
          },
        });
      } catch (activityError) {
        logger.warn('Failed to log unassignment activity', { error: activityError, id });
        // Don't fail the unassignment for activity logging issues
      }

      logger.info('Todo unassigned successfully', { id });
      return createSuccessResponse(updatedTodo);

    } catch (error) {
      logger.error('Error in unassignTodo', { error: error instanceof Error ? error.message : error, id });
      return createErrorResponse(error instanceof Error ? error.message : 'Unassign todo failed');
    }
  }

  async getAssignedTodos(userId: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting assigned todos', { userId });

      const { data, error } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('assigned_to', userId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to get assigned todos', { error: error.message, userId });
        throw error;
      }

      logger.debug('Assigned todos retrieved successfully', { userId, count: data.length });
      return createSuccessResponse(data as Todo[]);

    } catch (error) {
      logger.error('Error in getAssignedTodos', { error: error instanceof Error ? error.message : error, userId });
      return createErrorResponse(error instanceof Error ? error.message : 'Get assigned todos failed');
    }
  }

  async getProfileTasks(userId: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting profile tasks', { userId });

      const { data, error } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('created_by', userId)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to get profile tasks', { error: error.message, userId });
        throw error;
      }

      logger.debug('Profile tasks retrieved successfully', { userId, count: data.length });
      return createSuccessResponse(data as Todo[]);

    } catch (error) {
      logger.error('Error in getProfileTasks', { error: error instanceof Error ? error.message : error, userId });
      return createErrorResponse(error instanceof Error ? error.message : 'Get profile tasks failed');
    }
  }

  async getProfileSubtasks(parentId: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting profile subtasks', { parentId });

      const { data, error } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('parent_id', parentId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to get profile subtasks', { error: error.message, parentId });
        throw error;
      }

      logger.debug('Profile subtasks retrieved successfully', { parentId, count: data.length });
      return createSuccessResponse(data as Todo[]);

    } catch (error) {
      logger.error('Error in getProfileSubtasks', { error: error instanceof Error ? error.message : error, parentId });
      return createErrorResponse(error instanceof Error ? error.message : 'Get profile subtasks failed');
    }
  }
}

// Export singleton instance
export const todoAssignmentService = new TodoAssignmentService();
